/**
 * 项目文件中的现场照片查看区：点击缩略图后完整显示原图，支持缩放、拖动和连续翻页。
 * 复用项目灯箱的 1～6 倍缩放口径，但保留外层 Radix Dialog 的焦点管理与原有 testid。
 * 文件切换由父组件用文件 ID 作为 key 重建，避免上一张的缩放、偏移或加载错误影响下一张。
 */
import React, { useRef, useState } from "react";
import { ImageOff, Loader2, Maximize2, ZoomIn, ZoomOut } from "lucide-react";
import { Button } from "./ui/button";

/** url 为当前授权原图地址；name 用于无障碍描述；onStep 仅在未放大时接收横向滑动。 */
export default function ProjectFileImagePreview({ url, name, zh, onStep }) {
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [status, setStatus] = useState("loading");
  const gesture = useRef(null);
  const zoom = (value) => {
    const next = Math.min(6, Math.max(1, value));
    setScale(next);
    if (next === 1) setOffset({ x: 0, y: 0 });
  };
  const reset = () => { zoom(1); gesture.current = null; };

  // 使用指针捕获让鼠标拖出图片后仍能松手结束；放大时只平移，不把拖动误判为翻页。
  const start = (event) => {
    if (!event.isPrimary || event.button !== 0 || status !== "ready") return;
    gesture.current = { x: event.clientX, y: event.clientY, offset, id: event.pointerId };
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const move = (event) => {
    const from = gesture.current;
    if (!from || from.id !== event.pointerId || scale === 1) return;
    setOffset({ x: from.offset.x + event.clientX - from.x, y: from.offset.y + event.clientY - from.y });
  };
  const end = (event) => {
    const from = gesture.current;
    gesture.current = null;
    if (!from || from.id !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    const dx = event.clientX - from.x, dy = event.clientY - from.y;
    if (scale === 1 && Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) onStep(dx > 0 ? -1 : 1);
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-2" data-testid="file-preview-image-viewer" data-scale={scale.toFixed(2)}>
      <div className="flex flex-wrap items-center justify-center gap-1" role="group" aria-label={zh ? "照片缩放" : "Photo zoom"}>
        <Button type="button" variant="outline" size="icon" className="h-9 w-9" data-testid="file-preview-zoom-out" title={zh ? "缩小" : "Zoom out"} aria-label={zh ? "缩小" : "Zoom out"} disabled={scale <= 1 || status !== "ready"} onClick={() => zoom(scale / 1.25)}><ZoomOut className="h-4 w-4" aria-hidden="true" /></Button>
        <span className="w-14 text-center text-xs tabular-nums text-slate-600" data-testid="file-preview-zoom">{Math.round(scale * 100)}%</span>
        <Button type="button" variant="outline" size="icon" className="h-9 w-9" data-testid="file-preview-zoom-in" title={zh ? "放大" : "Zoom in"} aria-label={zh ? "放大" : "Zoom in"} disabled={scale >= 6 || status !== "ready"} onClick={() => zoom(scale * 1.25)}><ZoomIn className="h-4 w-4" aria-hidden="true" /></Button>
        <Button type="button" variant="outline" size="sm" className="h-9 gap-1.5" data-testid="file-preview-fit" disabled={status !== "ready"} onClick={reset}><Maximize2 className="h-4 w-4" aria-hidden="true" />{zh ? "适应窗口" : "Fit"}</Button>
      </div>
      <div className={`relative flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-lg bg-slate-100 ${scale > 1 ? "cursor-grab active:cursor-grabbing" : "cursor-zoom-in"}`}
        data-testid="file-preview-image-stage" style={{ touchAction: "none" }}
        onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerCancel={() => { gesture.current = null; }}
        onDoubleClick={() => { if (status === "ready") zoom(scale > 1 ? 1 : 2); }}>
        {status === "loading" && <div role="status" className="absolute inset-0 flex items-center justify-center gap-2 text-sm text-slate-500"><Loader2 className="h-5 w-5 animate-spin motion-reduce:animate-none" />{zh ? "照片加载中…" : "Loading photo…"}</div>}
        {status === "error" ? (
          <div role="alert" className="p-6 text-center text-sm text-slate-500"><ImageOff className="mx-auto mb-2 h-8 w-8" aria-hidden="true" />{zh ? "照片加载失败，可关闭后重试或打开原文件" : "Photo failed to load. Reopen or open the original file."}</div>
        ) : (
          <img src={url} alt={name} draggable={false} data-testid="file-preview-image" onLoad={() => setStatus("ready")} onError={() => setStatus("error")}
            className={`max-h-full max-w-full select-none object-contain ${status === "loading" ? "opacity-0" : ""}`}
            style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})` }} />
        )}
      </div>
    </div>
  );
}
